import React, { useEffect } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';

import styles from './Disaster.module.scss';
import Button from '../../../../components/button/button';
import FormCheckbox from '../../../../components/formCheckbox/FormCheckbox';
import FormNewCheckbox from '../../../../components/formCheckbox/FormNewCheckbox';
import Portal from '../../../../components/portal/Portal';
import StepperNavigation from '../../../../components/stepper/StepperNavigation';
import SectionContainer from '../../../../components/template/SectionContainer/SectionContainer';
import Subtitle from '../../../../components/template/Subtitle/Subtitle';
import Title from '../../../../components/template/Title/Title';
import { QuestionForm } from '../../../../models/questionModel';
import useFormStore from '../../../../store/FormStore';
import useQuestionStore from '../../../../store/QuestionStore';
import useStepperStore from '../../../../store/StepperStore';

interface DisasterProps {}

const Disaster: React.FC<DisasterProps> = ({}) => {
  const { setQuestion } = useQuestionStore();
  const { setStepper } = useStepperStore();
  const { setForm } = useFormStore();

  //#region Form
  const formMethods = useFormContext();
  const {
    getValues,
    reset,
    watch,
    formState: { isValid },
  } = formMethods;
  const { disaster } = watch();
  //#endregion

  useEffect(() => {
    reset(getValues());
  }, [disaster?.compulsory]);

  //#region Handler

  const getQuesWithMarks = () => {
    return DisasterData.impactQuestions.map((ques, quesIdx) => {
      const selectedCheckbox = ques.checkbox.find(
        (check) => check.marks == getValues('disaster')[quesIdx]
      );

      const selectedAnswer = selectedCheckbox?.isInput
        ? getValues(`${ques.id}_answer`)
        : selectedCheckbox?.label;

      return {
        question: ques.question,
        marks: selectedCheckbox?.marks || 0,
        answer: selectedAnswer || '',
      };
    });
  };

  const onHandleNavigation = (nav: 'next' | 'prev') => {
    if (!isValid && nav == 'next') return;

    const compulsoryQuestions = DisasterData.compulsoryQuestions.map((comp) => {
      return {
        answer: getValues('disaster.compulsory'),
        question: comp.question,
        questionNotes: [],
        answerNotes: [],
      };
    });

    const impactQuestions = getQuesWithMarks().map((q) => ({
      ...q,
      answerNotes: [],
      questionNotes: [],
    }));

    const probabilityQuestions = DisasterData.probabilityQuestions.map(
      (comp) => {
        return {
          answer:
            comp.inputs.find(
              (inp) => inp.value == Number(getValues('disaster.probability'))
            )?.label || '',
          question: comp.question,
          questionNotes: [],
          answerNotes: [],
          marks: Number(getValues('disaster.probability')),
        };
      }
    );

    const finalDisasterData = new QuestionForm(
      DisasterData.title,
      DisasterData.description,
      compulsoryQuestions,
      impactQuestions,
      probabilityQuestions
    );

    setQuestion({ disaster: finalDisasterData });

    setForm({ ...getValues() });

    setStepper(
      nav,
      undefined,
      disaster?.compulsory == '' ? undefined : disaster?.compulsory == 'Yes'
    );
  };

  const generateRandomData = (data: any) => {
    const result: any = {};

    data.impactQuestions.forEach((q: any, index: number) => {
      // ✅ exclude "Others"
      const validOptions = q.checkbox.filter((c: any) => !c.isInput);

      // randomly pick from only Yes/No
      const randomChoice =
        validOptions[Math.floor(Math.random() * validOptions.length)];

      result[index] = randomChoice.marks;
      result[`${index}_answer`] = ''; // always empty
    });

    // add compulsory + probability
    result.compulsory = Math.random() > 0.5 ? 'Yes' : 'Not applicable';
    result.probability = Math.random() > 0.5 ? 5 : 1; // only 1 or 5

    return result;
  };
  //#endregion

  const DisasterData = {
    title: 'Economic Disaster Risk',
    description:
      'An economic disaster is a sudden, calamitous event that seriously disrupts the functioning of an organization or individual and causes human, material, and economic or environmental losses that exceed the ability to cope using its own resources.',
    compulsoryQuestions: [
      {
        id: 'disaster.compulsory',
        question: 'Is your Company exposed to disaster risk?',
        inputs: [
          { value: 'Yes', label: 'Yes' },
          { value: 'Not applicable', label: 'Not applicable' },
        ],
      },
    ],
    probabilityQuestions: [
      {
        id: 'disaster.probability',
        question: 'How frequent is the Company facing the risk?',
        inputs: [
          { value: 1, label: 'Improbable' },
          { value: 2, label: 'Remote' },
          { value: 3, label: 'Fairly Likely' },
          { value: 4, label: 'Probable' },
          { value: 5, label: 'Very Likely' },
        ],
      },
    ],
    impactQuestions: [
      {
        id: 'disaster.0',
        question:
          'Does the organization have a Business Continuity Plan (BCP) to manage economic or disaster shocks?',
        checkbox: [
          { label: 'Yes', marks: 5 },
          { label: 'No', marks: 1 },
          { label: 'Others', marks: 3, isInput: true },
        ],
      },
      {
        id: 'disaster.1',
        question:
          'Has the company conducted stress testing or scenario planning for economic downturns?',
        checkbox: [
          { label: 'Yes', marks: 5 },
          { label: 'No', marks: 1 },
          { label: 'Others', marks: 3, isInput: true },
        ],
      },
      {
        id: 'disaster.2',
        question:
          'Does the company maintain sufficient cash reserves or credit facilities to withstand economic shocks?',
        checkbox: [
          { label: 'Yes', marks: 5 },
          { label: 'No', marks: 1 },
          { label: 'Others', marks: 3, isInput: true },
        ],
      },
      {
        id: 'disaster.3',
        question:
          'Is there a hedging or risk management strategy for currency and commodity price fluctuations?',
        checkbox: [
          { label: 'Yes', marks: 5 },
          { label: 'No', marks: 1 },
          { label: 'Others', marks: 3, isInput: true },
        ],
      },
      {
        id: 'disaster.4',
        question:
          'Does the company have measures to support employees during economic crises (e.g., wage subsidies, flexible arrangements)?',
        checkbox: [
          { label: 'Yes', marks: 5 },
          { label: 'No', marks: 1 },
          { label: 'Others', marks: 3, isInput: true },
        ],
      },
      {
        id: 'disaster.5',
        question:
          'Is succession planning in place to ensure continuity if leadership is impacted during a crisis?',
        checkbox: [
          { label: 'Yes', marks: 5 },
          { label: 'No', marks: 1 },
          { label: 'Others', marks: 3, isInput: true },
        ],
      },
      {
        id: 'disaster.6',
        question:
          'Does management actively monitor and comply with government relief measures (e.g., wage subsidy programs, tax reliefs)?',
        checkbox: [
          { label: 'Yes', marks: 5 },
          { label: 'No', marks: 1 },
          { label: 'Others', marks: 3, isInput: true },
        ],
      },
      {
        id: 'disaster.7',
        question:
          'Is the organization prepared for new regulatory requirements that may arise during economic crises?',
        checkbox: [
          { label: 'Yes', marks: 5 },
          { label: 'No', marks: 1 },
          { label: 'Others', marks: 3, isInput: true },
        ],
      },
      {
        id: 'disaster.8',
        question:
          'Are properties and assets insured against floods, fires, and other natural disasters common in Malaysia?',
        checkbox: [
          { label: 'Yes', marks: 5 },
          { label: 'No', marks: 1 },
          { label: 'Others', marks: 3, isInput: true },
        ],
      },
      {
        id: 'disaster.9',
        question:
          'Does the company assess climate change risks that could affect the Malaysian economy and operations (e.g., flooding, drought)?',
        checkbox: [
          { label: 'Yes', marks: 5 },
          { label: 'No', marks: 1 },
          { label: 'Others', marks: 3, isInput: true },
        ],
      },
    ],
  };

  return (
    <FormProvider {...formMethods}>
      <div className={styles.container}>
        <SectionContainer>
          <Title>{DisasterData.title}</Title>
          <Subtitle>{DisasterData.description}</Subtitle>

          {DisasterData.compulsoryQuestions.map((ques, idx) => (
            <FormCheckbox
              id={ques.id}
              key={idx}
              label={ques.question}
              options={ques.inputs}
              required
            />
          ))}
        </SectionContainer>

        {disaster?.compulsory && disaster?.compulsory == 'Yes' && (
          <div>Economic Disaster Risk Assessment Questionnaire</div>
        )}

        <SectionContainer>
          {disaster?.compulsory &&
            disaster?.compulsory == 'Yes' &&
            DisasterData.impactQuestions.map((ques, idx) => (
              <div key={idx}>
                <div>
                  {idx + 1}. {ques.question}
                </div>
                {ques.checkbox.map((check, index) => (
                  <FormNewCheckbox
                    key={index}
                    id={ques.id}
                    label={check.label}
                    value={check.marks}
                    required
                    textInput={{
                      id: check?.isInput ? `${ques.id}_answer` : '',
                      placeholder: check?.label,
                    }}
                  />
                ))}
              </div>
            ))}
        </SectionContainer>
      </div>

      <StepperNavigation
        isValid={isValid}
        onPrev={() => onHandleNavigation('prev')}
        onNext={() => onHandleNavigation('next')}
      />

      <Portal root="tester">
        <Button
          isCircle
          onClick={() => {
            reset({
              ...getValues(),
              disaster: {
                ...getValues().disaster,
                ...generateRandomData(DisasterData),
              },
            });
          }}
        >
          Random
        </Button>
      </Portal>
    </FormProvider>
  );
};

export default Disaster;
